import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  ArrowLeft, Calendar, User, Eye, BookOpen, Quote, 
  Loader2, Copy, FileText, ExternalLink, Building, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const articleTypeLabels = {
  research: 'Recherche empirique',
  case_study: 'Étude de cas',
  review: 'Revue de littérature',
  practice_note: 'Note de pratique',
  protocol: 'Protocole'
};

export default function ArticleDetailPage() {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [citation, setCitation] = useState(null);
  const [citationMetrics, setCitationMetrics] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [articleId]);

  useEffect(() => {
    if (article?.status === 'published') {
      fetchCitation();
      fetchCitationMetrics();
    }
  }, [article]);

  const fetchArticle = async () => {
    try {
      const res = await fetch(`${API_URL}/api/articles/${articleId}`);
      if (res.ok) {
        setArticle(await res.json());
      } else if (res.status === 404) {
        navigate('/articles');
      }
    } catch (e) {
      console.error('Failed to fetch article:', e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCitation = async () => {
    try {
      const res = await fetch(`${API_URL}/api/articles/${articleId}/citation?format=apa`);
      if (res.ok) {
        setCitation(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch citation:', e);
    }
  };

  const fetchCitationMetrics = async () => {
    try {
      const res = await fetch(`${API_URL}/api/citations/article/${articleId}`);
      if (res.ok) {
        setCitationMetrics(await res.json());
      }
    } catch (e) {
      console.error('Failed to fetch citation metrics:', e);
    }
  };

  const copyCitation = async (format) => {
    try {
      const res = await fetch(`${API_URL}/api/articles/${articleId}/citation?format=${format}`);
      if (res.ok) {
        const data = await res.json();
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(data.citation);
        } else {
          const textArea = document.createElement('textarea');
          textArea.value = data.citation;
          textArea.style.position = 'fixed';
          textArea.style.left = '-9999px';
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand('copy');
          document.body.removeChild(textArea);
        }
        toast.success(`Citation ${format.toUpperCase()} copiée`);
      } else {
        throw new Error('Failed to fetch citation');
      }
    } catch (e) {
      console.error('Copy error:', e);
      toast.error('Erreur lors de la copie. Veuillez copier manuellement.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Article non trouvé</p>
      </div>
    );
  }

  const formattedDate = new Date(article.published_at || article.created_at).toLocaleDateString('fr-FR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <div data-testid="article-detail-page" className="min-h-screen py-8 px-6 md:px-12">
      <div className="max-w-4xl mx-auto">
        {/* Back button */}
        <Link to="/articles" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft className="w-4 h-4" />
          Retour aux articles
        </Link>

        <article>
          <header className="mb-8">
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="secondary">
                {articleTypeLabels[article.article_type] || article.article_type}
              </Badge>
              {article.volume && article.issue && (
                <Badge variant="outline" className="text-xs">
                  Vol. {article.volume}, N° {article.issue}
                </Badge>
              )}
              {article.keywords.map((keyword, idx) => (
                <span key={idx} className="text-xs px-2 py-0.5 bg-muted rounded-full text-muted-foreground">
                  {keyword}
                </span>
              ))}
            </div>

            <h1 className="font-serif text-3xl md:text-4xl font-bold leading-tight mb-6">
              {article.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-sm text-muted-foreground mb-4">
              <div className="flex items-center gap-2">
                <User className="w-4 h-4" />
                <span>{article.author_name}</span>
                {article.author_orcid && (
                  <a 
                    href={`https://orcid.org/${article.author_orcid}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                    title="ORCID"
                  >
                    <img src="https://orcid.org/assets/vectors/orcid.logo.icon.svg" alt="ORCID" className="w-4 h-4" />
                  </a>
                )}
              </div>
              {article.author_institution && (
                <div className="flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  <span>{article.author_institution}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{formattedDate}</span>
              </div>
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4" />
                <span>{article.views} vues</span>
              </div>
              {citationMetrics && citationMetrics.times_cited > 0 && (
                <div className="flex items-center gap-2" data-testid="citation-count">
                  <TrendingUp className="w-4 h-4 text-primary" />
                  <span className="text-primary font-medium">
                    Cité {citationMetrics.times_cited} fois
                  </span>
                </div>
              )}
            </div>

            {/* DOI Badge */}
            {article.doi && (
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium text-muted-foreground">DOI:</span>
                <a 
                  href={`https://doi.org/${article.doi}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline flex items-center gap-1"
                >
                  {article.doi}
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </header>

          {/* Citation Card */}
          {article.status === 'published' && citation && (
            <Card className="mb-8 border-primary/20">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5" />
                    Citer cet article
                  </CardTitle>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2">
                        <Copy className="w-4 h-4" />
                        Copier
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => copyCitation('apa')}>
                        Format APA
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => copyCitation('bibtex')}>
                        Format BibTeX
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardHeader>
              <CardContent>
                <div className="p-3 bg-muted/50 rounded-lg font-mono text-sm">
                  {citation.citation}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Cited by section */}
          {citationMetrics && citationMetrics.times_cited > 0 && (
            <Card className="mb-8 border-primary/10" data-testid="cited-by-section">
              <CardHeader className="pb-3">
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-primary" />
                  Cité par ({citationMetrics.times_cited})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {citationMetrics.citations_received.map((cit) => (
                    <li key={cit.citation_id}>
                      <Link
                        to={`/articles/${cit.citing_article_id}`}
                        className="text-sm text-primary hover:underline"
                      >
                        {cit.citing_article_title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Abstract */}
          <Card className="mb-8 bg-muted/30">
            <CardHeader>
              <CardTitle className="font-serif text-lg flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Résumé
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground italic leading-relaxed">
                {article.abstract}
              </p>
            </CardContent>
          </Card>

          {/* Content with Markdown */}
          <div className="article-content prose prose-lg max-w-none mb-12">
            <ReactMarkdown 
              remarkPlugins={[remarkGfm]}
              components={{
                h1: ({children}) => <h1 className="font-serif text-3xl font-bold mt-8 mb-4 text-foreground">{children}</h1>,
                h2: ({children}) => <h2 className="font-serif text-2xl font-semibold mt-6 mb-3 text-foreground">{children}</h2>,
                h3: ({children}) => <h3 className="font-serif text-xl font-semibold mt-5 mb-2 text-foreground">{children}</h3>,
                p: ({children}) => <p className="mb-4 leading-relaxed text-foreground">{children}</p>,
                ul: ({children}) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
                li: ({children}) => <li className="mb-1 text-foreground">{children}</li>,
                blockquote: ({children}) => (
                  <blockquote className="border-l-4 border-primary pl-4 italic my-4 text-muted-foreground">
                    {children}
                  </blockquote>
                ),
                code: ({inline, children}) => 
                  inline ? (
                    <code className="bg-muted px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
                  ) : (
                    <pre className="bg-muted p-4 rounded-lg overflow-x-auto">
                      <code className="text-sm font-mono">{children}</code>
                    </pre>
                  ),
                a: ({href, children}) => (
                  <a href={href} className="text-primary underline hover:text-primary/80" target="_blank" rel="noopener noreferrer">
                    {children}
                  </a>
                ),
                table: ({children}) => (
                  <div className="overflow-x-auto my-4">
                    <table className="min-w-full border-collapse border border-border">{children}</table>
                  </div>
                ),
                th: ({children}) => <th className="border border-border px-4 py-2 bg-muted font-semibold">{children}</th>,
                td: ({children}) => <td className="border border-border px-4 py-2">{children}</td>,
              }}
            >
              {article.content}
            </ReactMarkdown>
          </div>

          {/* References */}
          {article.references && article.references.length > 0 && (
            <Card className="mb-12">
              <CardHeader>
                <CardTitle className="font-serif text-lg flex items-center gap-2">
                  <Quote className="w-5 h-5" />
                  Références bibliographiques
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ol className="list-decimal list-inside space-y-2">
                  {article.references.map((ref, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground">
                      {ref}
                    </li>
                  ))}
                </ol>
              </CardContent>
            </Card>
          )}
        </article>
      </div>
    </div>
  );
}
